import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { useAsyncJobStore, useUnacknowledgedFailureCount } from 'src/stores/async-jobs'
import { ActiveTasksList } from 'src/components/layout/active-tasks-list'

/**
 * Floating active-tasks pill. Active and failed background jobs surface here —
 * not in the side nav — as an ephemeral pill that auto-shows while work is
 * running (or a failure is unacknowledged) and auto-hides when idle.
 *
 * Anchored bottom-right so it never overlaps the left sidebar at any width.
 * Desktop-only: on mobile the bottom-bar alerts flyout covers the same jobs, so
 * the pill is hidden there to avoid a duplicate surface. Reuses
 * `ActiveTasksList` for the body (per-job cancel, per-failure dismiss +
 * dismiss-all).
 */
export const ActiveTasksWidget = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const active = useAsyncJobStore.use.active()
  const activeCount = active.length
  const failureCount = useUnacknowledgedFailureCount()

  const hasActive = activeCount > 0
  const hasFailures = failureCount > 0
  const isVisible = hasActive || hasFailures

  // Collapse the panel whenever the pill auto-hides, so it doesn't reopen
  // stale the next time work appears.
  useEffect(() => {
    if (!isVisible) setIsOpen(false)
  }, [isVisible])

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  // Auto-hide when nothing is active or failed.
  if (!isVisible) return null

  return (
    <div
      ref={containerRef}
      data-testid="active-tasks-pill"
      className="fixed bottom-4 right-4 z-toast hidden flex-col items-end sm:flex"
    >
      {isOpen && (
        <div className="mb-2 w-80 max-w-[90vw]">
          <ActiveTasksList />
        </div>
      )}

      <Button
        variant={hasFailures ? 'danger' : 'primary'}
        shape="circle"
        size="sm"
        onClick={() => setIsOpen((prev) => !prev)}
        className="gap-1.5 text-sm font-medium shadow-lg transition-colors"
      >
        {hasActive ? (
          <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
        )}
        <span
          data-testid="active-tasks-pill-count"
          aria-label={
            hasActive
              ? t('asyncJobs.activeCount', { count: activeCount })
              : t('asyncJobs.failedIndicator', { count: failureCount })
          }
        >
          {hasActive ? activeCount : failureCount}
        </span>
      </Button>
    </div>
  )
}
